import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ModalWindow from '@common/components/modal/modal-window';
import ModalContent from '@common/components/modal/modal-content';
import DataTable from '@common/components/datatable';
import Placeholder from '@common/components/placeholder';
import Spinner from '@common/components/spinner';
import * as integrationCommunitiesSelector from '../../selectors/integration-communities';
import {
  importCommunity,
  importCommunitiesBulk,
  fetchIntegrationCommunities,
} from '../../actions/integration-communities';
import useImportCommunitiesColumn from './useImportCommunitiesColumn';
import { AlertService } from '@common/services/alert';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  organisationId: string;
};

export const ImportCommunitiesModal = ({
  isOpen,
  onClose,
  organisationId,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const unlinkedCommunities = useSelector(
    integrationCommunitiesSelector.getUnlinkedCommunities
  );

  const isLoading = useSelector(integrationCommunitiesSelector.getIntegrationCommunitiesLoading);
  const [isImporting, setIsImporting] = React.useState(false);

  useEffect(() => {
    if (isOpen && organisationId) {
      dispatch(fetchIntegrationCommunities(organisationId));
    }
  }, [isOpen, organisationId, dispatch]);

  const handleImportCommunity = async (community: any) => {
    try {
      await dispatch(
        importCommunity(organisationId, {
          name: community.name,
          external_id: [community.externalId],
        })
      );
      AlertService.success(
        t('organisation:import_communities_success', {
          name: community.name,
        })
      );
    } catch (error) {
      AlertService.error(t('organisation:import_communities_error'));
    }
  };

  const handleImportAll = async () => {
    setIsImporting(true);
    try {
      const communitiesData = unlinkedCommunities.map((group) => ({
        name: group.name,
        external_id: Array.isArray(group.externalId)
          ? group.externalId
          : [group.externalId],
      }));
      await dispatch(importCommunitiesBulk(organisationId, communitiesData));
      AlertService.success(t('organisation:import_communities_bulk_success'));
      onClose();
    } catch (error) {
      AlertService.error(t('organisation:import_communities_bulk_error'));
    } finally {
      setIsImporting(false);
    }
  };

  const columns = useImportCommunitiesColumn({
    onImportCommunity: handleImportCommunity,
  });

  return (
    <ModalWindow show={isOpen} onHide={onClose} size="small">
      <ModalContent
        title={t('organisation:import_communities_title')}
        onHide={onClose}
        confirmButtonText={t('organisation:import_communities_import_all')}
        onConfirm={handleImportAll}
        confirmButtonLoading={isImporting}
        confirmButtonDisabled={unlinkedCommunities.length === 0 || isLoading}
      >
        {isLoading ? (
          <div className="tw-flex tw-justify-center tw-items-center">
            <Spinner centered />
          </div>
        ) : (
          <DataTable
            columns={columns as any}
            data={unlinkedCommunities}
            emptyView={
              <Placeholder
                title={t('organisation:import_communities_no_unlinked')}
                description={t('organisation:import_communities_no_unlinked')}
              />
            }
          />
        )}
      </ModalContent>
    </ModalWindow>
  );
};
